import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { motion, AnimatePresence, Reorder } from "framer-motion";
import {
  Headphones,
  LogOut,
  QrCode,
  Search,
  Radio,
  CheckCircle2,
  Clock,
  Trash2,
  Play,
  RotateCcw,
  Download,
  Copy,
  X,
  Music2,
  ListMusic,
  GripVertical,
  MonitorPlay,
  BarChart3,
  Trophy,
  CalendarDays,
  Plus,
  MapPin,
  ImageIcon,
  Upload,
  Save,
  Phone,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS_META = {
  pending: { label: "In Attesa", color: "text-amber-400", dot: "bg-amber-400", ring: "border-amber-500/30 bg-amber-500/10" },
  playing: { label: "In Onda", color: "text-cyan-400", dot: "bg-cyan-400", ring: "border-cyan-500/30 bg-cyan-500/10" },
  done: { label: "Cantata", color: "text-emerald-400", dot: "bg-emerald-400", ring: "border-emerald-500/30 bg-emerald-500/10" },
};

export default function DashboardPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [eventFilter, setEventFilter] = useState("all");
  const [activeEvent, setActiveEvent] = useState("");
  const [query, setQuery] = useState("");
  const [showQR, setShowQR] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [summary, setSummary] = useState(null);
  const [showEvents, setShowEvents] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventForm, setEventForm] = useState({ title: "", date: "", place: "" });
  const [showHome, setShowHome] = useState(false);
  const [contacts, setContacts] = useState({ phone: "", whatsapp: "", email: "", instagram: "", facebook: "", tiktok: "", youtube: "" });
  const [heroFile, setHeroFile] = useState(null);
  const [heroUploading, setHeroUploading] = useState(false);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [eventName, setEventName] = useState("Karaoke Night");
  const prevCount = useRef(0);
  const bookingsRef = useRef([]);
  const draggingRef = useRef(false);
  const loggingOutRef = useRef(false);

  const bookingUrl = `${window.location.origin}/prenota`;

  useEffect(() => {
    bookingsRef.current = bookings;
  }, [bookings]);

  useEffect(() => {
    api
      .get("/event")
      .then(({ data }) => setEventName(data.event_name))
      .catch(() => {});
    api
      .get("/active-event")
      .then(({ data }) => setActiveEvent(data.active_event || ""))
      .catch(() => {});
  }, []);

  const saveActiveEvent = async (title) => {
    setActiveEvent(title);
    try {
      await api.put("/active-event", { event_title: title });
      toast.success(title ? `Evento di stasera: ${title}` : "Evento di stasera rimosso");
    } catch {
      toast.error("Errore salvataggio evento attivo");
    }
  };

  const saveEvent = async () => {
    try {
      await api.put("/settings", { event_name: eventName });
      toast.success("Nome serata salvato");
    } catch {
      toast.error("Errore salvataggio nome serata");
    }
  };

  const openSummary = async () => {
    try {
      const { data } = await api.get("/stats");
      setSummary(data);
      setShowSummary(true);
    } catch {
      toast.error("Errore caricamento riepilogo");
    }
  };

  const loadEvents = useCallback(async () => {
    try {
      const { data } = await api.get("/events");
      setEvents(data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const addEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title.trim()) {
      toast.error("Inserisci il nome dell'evento");
      return;
    }
    try {
      await api.post("/events", eventForm);
      setEventForm({ title: "", date: "", place: "" });
      toast.success("Evento aggiunto");
      loadEvents();
    } catch {
      toast.error("Errore aggiunta evento");
    }
  };

  const deleteEvent = async (id) => {
    setEvents((ev) => ev.filter((x) => x.id !== id));
    try {
      await api.delete(`/events/${id}`);
    } catch {
      toast.error("Errore rimozione evento");
      loadEvents();
    }
  };

  const openHome = async () => {
    try {
      const { data } = await api.get("/home");
      setContacts(data.contacts);
    } catch {
      /* ignore */
    }
    setShowHome(true);
  };

  const saveContacts = async () => {
    try {
      await api.put("/home", contacts);
      toast.success("Contatti aggiornati");
    } catch {
      toast.error("Errore salvataggio contatti");
    }
  };

  const uploadHero = async () => {
    if (!heroFile) {
      toast.error("Scegli prima un'immagine");
      return;
    }
    setHeroUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", heroFile);
      await api.post("/home/hero-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sfondo aggiornato! Ricarica la Home per vederlo");
      setHeroFile(null);
    } catch (e) {
      toast.error("Errore caricamento immagine");
    } finally {
      setHeroUploading(false);
    }
  };

  const uploadBanner = async () => {
    if (!bannerFile) {
      toast.error("Scegli prima un'immagine");
      return;
    }
    setBannerUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", bannerFile);
      await api.post("/home/banner-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Banner aggiornato! Ricarica la Home per vederlo");
      setBannerFile(null);
    } catch (e) {
      toast.error("Errore caricamento immagine");
    } finally {
      setBannerUploading(false);
    }
  };

  const canDrag = filter === "all" && eventFilter === "all" && !query.trim();

  const load = useCallback(async () => {
    if (draggingRef.current || loggingOutRef.current) return;
    try {
      const { data } = await api.get("/bookings");
      setBookings((prev) => {
        if (loaded && data.length > prevCount.current) {
          toast.success("🎤 Nuova canzone prenotata!");
        }
        prevCount.current = data.length;
        return data;
      });
      setLoaded(true);
    } catch (err) {
      if (err.response?.status === 401 && !loggingOutRef.current) navigate("/dj/login");
    }
  }, [loaded, navigate]);

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  const setStatus = async (id, status) => {
    setBookings((b) => b.map((x) => (x.id === id ? { ...x, status } : x)));
    try {
      await api.patch(`/bookings/${id}/status`, { status });
    } catch {
      toast.error("Errore aggiornamento stato");
      load();
    }
  };

  const remove = async (id) => {
    setBookings((b) => b.filter((x) => x.id !== id));
    prevCount.current -= 1;
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Prenotazione rimossa");
    } catch {
      toast.error("Errore rimozione");
      load();
    }
  };

  const persistOrder = async () => {
    draggingRef.current = false;
    try {
      await api.put("/bookings/reorder", {
        ids: bookingsRef.current.map((b) => b.id),
      });
    } catch {
      toast.error("Errore riordino");
      load();
    }
  };

  const doLogout = async () => {
    loggingOutRef.current = true;
    navigate("/");
    await logout();
  };

  const stats = useMemo(
    () => ({
      total: bookings.length,
      pending: bookings.filter((b) => b.status === "pending").length,
      playing: bookings.filter((b) => b.status === "playing").length,
      done: bookings.filter((b) => b.status === "done").length,
    }),
    [bookings]
  );

  const eventOptions = useMemo(
    () => [...new Set(bookings.map((b) => b.evento).filter(Boolean))],
    [bookings]
  );

  const filtered = useMemo(() => {
    let list = bookings;
    if (filter !== "all") list = list.filter((b) => b.status === filter);
    if (eventFilter !== "all") list = list.filter((b) => (b.evento || "") === eventFilter);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (b) =>
          `${b.nome} ${b.cognome}`.toLowerCase().includes(q) ||
          b.titolo.toLowerCase().includes(q) ||
          b.artista.toLowerCase().includes(q)
      );
    }
    return list;
  }, [bookings, filter, eventFilter, query]);

  const downloadQR = () => {
    const canvas = document.querySelector("#qr-canvas canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = "karaoke-qrcode.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const copyLink = () => {
    navigator.clipboard.writeText(bookingUrl);
    toast.success("Link copiato!");
  };

  return (
    <div className="neon-grid min-h-screen w-full">
      {/* header */}
      <header className="glass sticky top-0 z-20 border-b border-slate-800/80">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2" data-testid="nav-logo">
            <div className="h-9 w-9 rounded-xl beam flex items-center justify-center">
              <Headphones className="h-5 w-5 text-white" />
            </div>
            <div className="leading-tight">
              <p className="font-display font-extrabold text-white text-sm sm:text-base">
                Console DJ
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400 flex items-center gap-1.5">
                <span className="relative inline-flex h-1.5 w-1.5">
                  <span className="live-dot absolute inline-flex h-1.5 w-1.5" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                </span>
                Aggiornamento live
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openHome}
              data-testid="nav-home-settings-button"
              className="flex items-center gap-2 border border-slate-700 hover:border-purple-500 hover:text-purple-300 text-white rounded-xl px-3 sm:px-4 py-2 text-sm transition-colors"
            >
              <ImageIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Aspetto Home</span>
            </button>
            <button
              onClick={() => setShowEvents(true)}
              data-testid="nav-events-button"
              className="flex items-center gap-2 border border-slate-700 hover:border-fuchsia-500 hover:text-fuchsia-300 text-white rounded-xl px-3 sm:px-4 py-2 text-sm transition-colors"
            >
              <CalendarDays className="h-4 w-4" />
              <span className="hidden sm:inline">Eventi</span>
            </button>
            <button
              onClick={openSummary}
              data-testid="nav-summary-button"
              className="flex items-center gap-2 border border-slate-700 hover:border-purple-500 hover:text-purple-300 text-white rounded-xl px-3 sm:px-4 py-2 text-sm transition-colors"
            >
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Riepilogo</span>
            </button>
            <button
              onClick={() => navigate("/dj/stage")}
              data-testid="nav-stage-button"
              className="flex items-center gap-2 border border-slate-700 hover:border-cyan-500 hover:text-cyan-400 text-white rounded-xl px-3 sm:px-4 py-2 text-sm transition-colors"
            >
              <MonitorPlay className="h-4 w-4" />
              <span className="hidden sm:inline">Schermo Palco</span>
            </button>
            <button
              onClick={() => setShowQR(true)}
              data-testid="nav-qrcode-button"
              className="flex items-center gap-2 border border-slate-700 hover:border-pink-500 text-white rounded-xl px-3 sm:px-4 py-2 text-sm transition-colors"
            >
              <QrCode className="h-4 w-4" />
              <span className="hidden sm:inline">QR Code</span>
            </button>
            <a
              href="https://karaoke.jekodj.it/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-open-karaoke-link"
              className="flex items-center gap-2 border border-slate-700 hover:border-emerald-500 hover:text-emerald-400 text-white rounded-xl px-3 sm:px-4 py-2 text-sm transition-colors"
            >
              <Music2 className="h-4 w-4" />
              <span className="hidden sm:inline">Apri DJ Karaoke</span>
            </a>
            <button
              onClick={doLogout}
              data-testid="dj-logout-button"
              className="flex items-center gap-2 border border-slate-700 hover:border-red-500 hover:text-red-400 text-white rounded-xl px-3 sm:px-4 py-2 text-sm transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Esci</span>
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard testid="stat-total" icon={<ListMusic className="h-5 w-5" />} label="Totale" value={stats.total} accent="text-pink-400" />
          <StatCard testid="stat-pending" icon={<Clock className="h-5 w-5" />} label="In Attesa" value={stats.pending} accent="text-amber-400" />
          <StatCard testid="stat-playing" icon={<Radio className="h-5 w-5" />} label="In Onda" value={stats.playing} accent="text-cyan-400" />
          <StatCard testid="stat-done" icon={<CheckCircle2 className="h-5 w-5" />} label="Cantate" value={stats.done} accent="text-emerald-400" />
        </div>

        {/* controls */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <div className="flex flex-wrap gap-2">
            <FilterBtn testid="dashboard-filter-all" active={filter === "all"} onClick={() => setFilter("all")}>Tutti</FilterBtn>
            <FilterBtn testid="dashboard-filter-pending" active={filter === "pending"} onClick={() => setFilter("pending")}>In Attesa</FilterBtn>
            <FilterBtn testid="dashboard-filter-playing" active={filter === "playing"} onClick={() => setFilter("playing")}>In Onda</FilterBtn>
            <FilterBtn testid="dashboard-filter-done" active={filter === "done"} onClick={() => setFilter("done")}>Cantate</FilterBtn>
            {eventOptions.length > 0 && (
              <select
                data-testid="dashboard-event-filter"
                value={eventFilter}
                onChange={(e) => setEventFilter(e.target.value)}
                className="rounded-full bg-slate-950/60 border border-slate-700 focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-500/30 outline-none px-4 py-2 text-sm text-white transition-colors"
              >
                <option value="all" className="bg-slate-900">Tutti gli eventi</option>
                {eventOptions.map((ev) => (
                  <option key={ev} value={ev} className="bg-slate-900">{ev}</option>
                ))}
              </select>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              data-testid="dashboard-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca nome, canzone, artista…"
              className="w-full sm:w-72 rounded-xl bg-slate-950/60 border border-slate-700 focus:border-pink-500 focus:ring-2 focus:ring-pink-500/30 outline-none pl-9 pr-4 py-2.5 text-sm text-white placeholder:text-slate-600 transition-colors"
            />
          </div>
        </div>

        {/* table / list */}
        {canDrag && bookings.length > 1 && (
          <p className="flex items-center gap-2 text-xs text-slate-500 font-mono">
            <GripVertical className="h-3.5 w-3.5" /> Trascina le righe per ordinare la
            scaletta
          </p>
        )}
        <div className="neon-card rounded-2xl overflow-hidden" data-testid="dashboard-song-table">
          {/* header row (desktop) */}
          <div className="hidden lg:grid grid-cols-[1.4fr_2fr_1.4fr_1fr_1fr_auto] gap-4 px-6 py-4 border-b border-slate-800 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
            <span>Cliente</span>
            <span>Titolo Canzone</span>
            <span>Artista</span>
            <span>Versione</span>
            <span>Tavolo / Note</span>
            <span className="text-right">Azioni</span>
          </div>

          {filtered.length === 0 ? (
            <div className="py-16 text-center" data-testid="dashboard-empty">
              <Music2 className="h-10 w-10 text-slate-700 mx-auto mb-3" />
              <p className="text-slate-500">
                {loaded ? "Nessuna prenotazione qui" : "Caricamento…"}
              </p>
              <p className="text-slate-600 text-sm mt-1">
                Le canzoni prenotate appariranno qui in tempo reale
              </p>
            </div>
          ) : canDrag ? (
            <Reorder.Group as="div" axis="y" values={bookings} onReorder={setBookings}>
              {bookings.map((b) => (
                <Reorder.Item
                  as="div"
                  key={b.id}
                  value={b}
                  data-testid="booking-row"
                  onDragStart={() => {
                    draggingRef.current = true;
                  }}
                  onDragEnd={persistOrder}
                  className="grid grid-cols-1 lg:grid-cols-[1.4fr_2fr_1.4fr_1fr_1fr_auto] gap-2 lg:gap-4 px-5 lg:px-6 py-4 border-b border-slate-800/70 hover:bg-slate-900/40 transition-colors items-center bg-[#12121C]"
                >
                  <RowContent b={b} setStatus={setStatus} remove={remove} draggable />
                </Reorder.Item>
              ))}
            </Reorder.Group>
          ) : (
            <AnimatePresence initial={false}>
              {filtered.map((b, i) => (
                <motion.div
                  key={b.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25, delay: Math.min(i * 0.02, 0.2) }}
                  data-testid="booking-row"
                  className="grid grid-cols-1 lg:grid-cols-[1.4fr_2fr_1.4fr_1fr_1fr_auto] gap-2 lg:gap-4 px-5 lg:px-6 py-4 border-b border-slate-800/70 hover:bg-slate-900/40 transition-colors items-center"
                >
                  <RowContent b={b} setStatus={setStatus} remove={remove} />
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>
      </main>

      {/* QR modal */}
      <AnimatePresence>
        {showQR && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowQR(false)}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="qr-code-card"
              className="neon-card rounded-2xl p-7 max-w-sm w-full relative"
            >
              <button
                onClick={() => setShowQR(false)}
                data-testid="qr-close-button"
                className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="text-center">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-pink-400 mb-2">
                  Inquadra e prenota
                </p>
                <h3 className="font-display text-2xl font-black text-white text-glow mb-4">
                  {eventName}
                </h3>
                <div className="text-left mb-5">
                  <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
                    Nome della serata
                  </label>
                  <div className="flex gap-2">
                    <input
                      data-testid="event-name-input"
                      value={eventName}
                      onChange={(e) => setEventName(e.target.value)}
                      placeholder="Es. Venerdì Karaoke"
                      className="flex-1 rounded-xl bg-slate-950/60 border border-slate-700 focus:border-pink-500 focus:ring-2 focus:ring-pink-500/30 outline-none px-3 py-2.5 text-sm text-white placeholder:text-slate-600 transition-colors"
                    />
                    <button
                      onClick={saveEvent}
                      data-testid="event-name-save-button"
                      className="beam text-white text-sm font-semibold rounded-xl px-4 hover:opacity-90 transition"
                    >
                      Salva
                    </button>
                  </div>
                </div>
                <div id="qr-canvas" className="bg-white p-4 rounded-2xl inline-block">
                  <QRCodeCanvas
                    value={bookingUrl}
                    size={220}
                    level="H"
                    fgColor="#08080C"
                    bgColor="#ffffff"
                  />
                </div>
                <p className="mt-4 text-slate-400 text-sm break-all font-mono">
                  {bookingUrl}
                </p>
                <div className="mt-6 flex gap-3">
                  <button
                    onClick={downloadQR}
                    data-testid="qr-code-download-button"
                    className="flex-1 beam text-white font-semibold rounded-xl py-3 flex items-center justify-center gap-2 hover:opacity-90 transition"
                  >
                    <Download className="h-4 w-4" /> Scarica
                  </button>
                  <button
                    onClick={copyLink}
                    data-testid="qr-copy-button"
                    className="flex-1 border border-slate-700 hover:border-cyan-500 text-white rounded-xl py-3 flex items-center justify-center gap-2 transition-colors"
                  >
                    <Copy className="h-4 w-4" /> Copia link
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Home settings modal */}
      <AnimatePresence>
        {showHome && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowHome(false)}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="home-settings-card"
              className="neon-card rounded-2xl p-7 max-w-lg w-full relative max-h-[88vh] overflow-y-auto"
            >
              <button
                onClick={() => setShowHome(false)}
                data-testid="home-settings-close-button"
                className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-purple-400 mb-2">
                Aspetto Home
              </p>
              <h3 className="font-display text-2xl font-black text-white text-glow mb-6">
                Sfondo e contatti
              </h3>

              {/* Background upload */}
              <div className="mb-7">
                <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-3 flex items-center gap-2">
                  <ImageIcon className="h-3.5 w-3.5 text-pink-400" /> Foto di sfondo (hero)
                </p>
                <input
                  type="file"
                  accept="image/*"
                  data-testid="hero-file-input"
                  onChange={(e) => setHeroFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-slate-300 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-800 file:px-4 file:py-2 file:text-slate-200 hover:file:bg-slate-700"
                />
                {heroFile && <p className="text-xs text-slate-500 mt-2 truncate">{heroFile.name}</p>}
                <button
                  onClick={uploadHero}
                  disabled={heroUploading}
                  data-testid="hero-upload-button"
                  className="mt-3 w-full flex items-center justify-center gap-2 beam text-white font-semibold rounded-xl py-3 hover:opacity-90 transition disabled:opacity-60"
                >
                  <Upload className="h-4 w-4" /> {heroUploading ? "Caricamento…" : "Carica sfondo"}
                </button>
                <p className="text-[11px] text-slate-600 mt-2">JPG/PNG, max 6MB. Dopo il caricamento ricarica la Home.</p>
              </div>

              {/* Banner upload */}
              <div className="mb-7">
                <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-3 flex items-center gap-2">
                  <ImageIcon className="h-3.5 w-3.5 text-cyan-400" /> Foto banner ("Music Connects People")
                </p>
                <input
                  type="file"
                  accept="image/*"
                  data-testid="banner-file-input"
                  onChange={(e) => setBannerFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-slate-300 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-800 file:px-4 file:py-2 file:text-slate-200 hover:file:bg-slate-700"
                />
                {bannerFile && <p className="text-xs text-slate-500 mt-2 truncate">{bannerFile.name}</p>}
                <button
                  onClick={uploadBanner}
                  disabled={bannerUploading}
                  data-testid="banner-upload-button"
                  className="mt-3 w-full flex items-center justify-center gap-2 beam text-white font-semibold rounded-xl py-3 hover:opacity-90 transition disabled:opacity-60"
                >
                  <Upload className="h-4 w-4" /> {bannerUploading ? "Caricamento…" : "Carica banner"}
                </button>
                <p className="text-[11px] text-slate-600 mt-2">JPG/PNG, max 6MB. Dopo il caricamento ricarica la Home.</p>
              </div>

              {/* Contacts */}
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-3 flex items-center gap-2">
                <Phone className="h-3.5 w-3.5 text-emerald-400" /> Contatti in Home
              </p>
              <div className="space-y-3">
                {[
                  { k: "phone", ph: "Telefono (+39 ...)" },
                  { k: "whatsapp", ph: "WhatsApp (solo numero, es. 39392...)" },
                  { k: "email", ph: "Email" },
                  { k: "instagram", ph: "Link Instagram" },
                  { k: "facebook", ph: "Link Facebook" },
                  { k: "tiktok", ph: "Link TikTok" },
                  { k: "youtube", ph: "Link YouTube" },
                ].map((f) => (
                  <input
                    key={f.k}
                    data-testid={`contact-${f.k}-input`}
                    value={contacts[f.k] || ""}
                    onChange={(e) => setContacts((c) => ({ ...c, [f.k]: e.target.value }))}
                    placeholder={f.ph}
                    className="w-full rounded-xl bg-slate-950/60 border border-slate-700 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/30 outline-none px-4 py-2.5 text-sm text-white placeholder:text-slate-600 transition-colors"
                  />
                ))}
              </div>
              <button
                onClick={saveContacts}
                data-testid="contacts-save-button"
                className="mt-5 w-full flex items-center justify-center gap-2 beam text-white font-semibold rounded-xl py-3 hover:opacity-90 transition"
              >
                <Save className="h-4 w-4" /> Salva contatti
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Events modal */}
      <AnimatePresence>
        {showEvents && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowEvents(false)}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="events-card"
              className="neon-card rounded-2xl p-7 max-w-lg w-full relative max-h-[85vh] overflow-y-auto"
            >
              <button
                onClick={() => setShowEvents(false)}
                data-testid="events-close-button"
                className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-fuchsia-400 mb-2">
                Gestione Eventi
              </p>
              <h3 className="font-display text-2xl font-black text-white text-glow mb-6">
                Le tue serate
              </h3>

              <div className="mb-6 rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-4">
                <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-cyan-400 mb-2">
                  Evento di stasera (pre-selezionato nella prenotazione)
                </label>
                <select
                  data-testid="active-event-select"
                  value={activeEvent}
                  onChange={(e) => saveActiveEvent(e.target.value)}
                  className="w-full rounded-xl bg-slate-950/60 border border-slate-700 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30 outline-none px-4 py-2.5 text-sm text-white transition-colors"
                >
                  <option value="" className="bg-slate-900">Nessuno (scelta libera)</option>
                  {events.map((ev) => (
                    <option key={ev.id} value={ev.title} className="bg-slate-900">
                      {ev.title}{ev.place ? ` — ${ev.place}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <form onSubmit={addEvent} className="space-y-3 mb-6">
                <input
                  data-testid="event-title-input"
                  value={eventForm.title}
                  onChange={(e) => setEventForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Nome evento (es. Karaoke Night)"
                  className="w-full rounded-xl bg-slate-950/60 border border-slate-700 focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-500/30 outline-none px-4 py-2.5 text-sm text-white placeholder:text-slate-600 transition-colors"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    data-testid="event-date-input"
                    type="date"
                    value={eventForm.date}
                    onChange={(e) => setEventForm((f) => ({ ...f, date: e.target.value }))}
                    className="w-full rounded-xl bg-slate-950/60 border border-slate-700 focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-500/30 outline-none px-4 py-2.5 text-sm text-white transition-colors"
                  />
                  <input
                    data-testid="event-place-input"
                    value={eventForm.place}
                    onChange={(e) => setEventForm((f) => ({ ...f, place: e.target.value }))}
                    placeholder="Luogo"
                    className="w-full rounded-xl bg-slate-950/60 border border-slate-700 focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-500/30 outline-none px-4 py-2.5 text-sm text-white placeholder:text-slate-600 transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="event-add-button"
                  className="w-full flex items-center justify-center gap-2 beam text-white font-semibold rounded-xl py-3 hover:opacity-90 transition"
                >
                  <Plus className="h-4 w-4" /> Aggiungi evento
                </button>
              </form>

              <div className="space-y-2">
                {events.length === 0 ? (
                  <p className="text-slate-600 text-sm text-center py-4" data-testid="events-empty">
                    Nessun evento. Aggiungine uno qui sopra.
                  </p>
                ) : (
                  events.map((ev) => (
                    <div
                      key={ev.id}
                      data-testid="event-row"
                      className="flex items-center gap-3 rounded-lg bg-slate-900/50 border border-slate-800 px-3 py-2.5"
                    >
                      <div className="text-center shrink-0 w-16">
                        <CalendarDays className="h-4 w-4 text-fuchsia-400 mx-auto" />
                        <p className="font-mono text-[10px] text-slate-400 mt-1">{ev.date || "—"}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold text-sm truncate">{ev.title}</p>
                        {ev.place && (
                          <p className="text-slate-400 text-xs flex items-center gap-1 truncate">
                            <MapPin className="h-3 w-3" /> {ev.place}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => deleteEvent(ev.id)}
                        data-testid="event-delete-button"
                        className="h-8 w-8 flex items-center justify-center rounded-lg border border-slate-700 text-slate-400 hover:text-red-400 hover:border-red-500/50 transition-colors shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Summary modal */}
      <AnimatePresence>
        {showSummary && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowSummary(false)}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="summary-card"
              className="neon-card rounded-2xl p-7 max-w-lg w-full relative max-h-[85vh] overflow-y-auto"
            >
              <button
                onClick={() => setShowSummary(false)}
                data-testid="summary-close-button"
                className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-pink-400 mb-2">
                Riepilogo Serata
              </p>
              <h3 className="font-display text-2xl font-black text-white text-glow mb-6">
                {eventName}
              </h3>

              <div className="grid grid-cols-3 gap-3 mb-7">
                <SummaryStat label="Prenotate" value={summary?.total ?? 0} accent="text-pink-400" />
                <SummaryStat label="Cantate" value={summary?.done ?? 0} accent="text-emerald-400" />
                <SummaryStat
                  label="In coda"
                  value={(summary?.pending ?? 0) + (summary?.playing ?? 0)}
                  accent="text-cyan-400"
                />
              </div>

              <SummaryList title="Brani più richiesti" items={summary?.top_songs} />
              <div className="h-5" />
              <SummaryList title="Artisti più richiesti" items={summary?.top_artists} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SummaryStat({ label, value, accent }) {
  return (
    <div className="rounded-xl bg-slate-950/50 border border-slate-800 p-4 text-center">
      <p className={`font-display text-3xl font-black ${accent}`}>{value}</p>
      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400 mt-1">
        {label}
      </p>
    </div>
  );
}

function SummaryList({ title, items }) {
  return (
    <div>
      <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-3 flex items-center gap-2">
        <Trophy className="h-3.5 w-3.5 text-amber-400" /> {title}
      </p>
      {!items || items.length === 0 ? (
        <p className="text-slate-600 text-sm">Nessun dato ancora</p>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div
              key={it.label}
              className="flex items-center gap-3 rounded-lg bg-slate-900/50 border border-slate-800 px-3 py-2.5"
            >
              <span className="font-display font-black text-lg text-pink-500 w-6 shrink-0">
                {i + 1}
              </span>
              <span className="flex-1 min-w-0 truncate text-white text-sm">{it.label}</span>
              <span className="shrink-0 text-xs font-mono px-2 py-1 rounded-md bg-slate-800 border border-slate-700 text-slate-300">
                {it.count}×
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, accent, testid }) {
  return (
    <div className="neon-card rounded-2xl p-5" data-testid={testid}>
      <div className={`flex items-center gap-2 ${accent}`}>{icon}
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
          {label}
        </span>
      </div>
      <p className="mt-3 font-display text-3xl font-black text-white">{value}</p>
    </div>
  );
}

function FilterBtn({ children, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
        active
          ? "beam text-white shadow-[0_0_20px_rgba(236,72,153,0.3)]"
          : "border border-slate-700 text-slate-300 hover:border-pink-500/50"
      }`}
    >
      {children}
    </button>
  );
}

function IconBtn({ children, onClick, title, className = "", testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`h-9 w-9 flex items-center justify-center rounded-lg border border-slate-700 text-slate-400 transition-colors ${className}`}
    >
      {children}
    </button>
  );
}

function RowContent({ b, setStatus, remove, draggable = false }) {
  const meta = STATUS_META[b.status];
  return (
    <>
      <div className="flex items-center gap-3">
        {draggable && (
          <GripVertical
            data-testid="drag-handle"
            className="h-4 w-4 text-slate-600 cursor-grab active:cursor-grabbing shrink-0"
          />
        )}
        <span className={`hidden lg:inline-flex h-2 w-2 rounded-full ${meta.dot}`} />
        <div>
          <p className="text-white font-semibold leading-tight">
            {b.nome} {b.cognome}
          </p>
          {b.evento && (
            <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-mono uppercase tracking-wider text-fuchsia-300 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded px-1.5 py-0.5">
              {b.evento}
            </span>
          )}
          <span
            className={`lg:hidden inline-flex items-center gap-1.5 mt-1 ml-1 text-[11px] font-mono uppercase tracking-wider ${meta.color}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} /> {meta.label}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Music2 className="h-4 w-4 text-pink-400 shrink-0 lg:hidden" />
        <span className="text-white font-medium">{b.titolo}</span>
      </div>

      <span className="text-slate-300">{b.artista}</span>

      <span>
        <span className="inline-block text-xs font-mono px-2.5 py-1 rounded-md bg-slate-800/70 border border-slate-700 text-slate-300">
          {b.versione}
        </span>
      </span>

      <span className="text-slate-400 text-sm">
        {b.tavolo && <span className="text-slate-200">Tavolo {b.tavolo}</span>}
        {b.tavolo && b.note && " · "}
        {b.note}
        {!b.tavolo && !b.note && <span className="text-slate-600">—</span>}
      </span>

      <div className="flex items-center gap-1.5 lg:justify-end mt-2 lg:mt-0">
        {b.status !== "playing" && (
          <IconBtn testid="action-play" title="Segna In Onda" onClick={() => setStatus(b.id, "playing")} className="hover:text-cyan-400 hover:border-cyan-500/50">
            <Play className="h-4 w-4" />
          </IconBtn>
        )}
        {b.status !== "done" && (
          <IconBtn testid="action-done" title="Segna Cantata" onClick={() => setStatus(b.id, "done")} className="hover:text-emerald-400 hover:border-emerald-500/50">
            <CheckCircle2 className="h-4 w-4" />
          </IconBtn>
        )}
        {b.status !== "pending" && (
          <IconBtn testid="action-reset" title="Riporta In Attesa" onClick={() => setStatus(b.id, "pending")} className="hover:text-amber-400 hover:border-amber-500/50">
            <RotateCcw className="h-4 w-4" />
          </IconBtn>
        )}
        <IconBtn testid="action-delete" title="Rimuovi" onClick={() => remove(b.id)} className="hover:text-red-400 hover:border-red-500/50">
          <Trash2 className="h-4 w-4" />
        </IconBtn>
      </div>
    </>
  );
}
